import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import './styles/eventTilmeld.css';

function EventTilmeld({ eventId, onClose, onTilmeldingGennemført, maxDeltagere }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    virksomhedsnavn: '',
    cvr: '',
    faktureringsType: 'email',
    faktureringsemail: '',
    ean: '',
    antalDeltagere: 1,
    deltagere: []
  });
  const [currentTilmeldte, setCurrentTilmeldte] = useState(0);

  useEffect(() => {
    const hentTilmeldinger = async () => {
      try {
        const q = query(collection(db, 'tilmeldinger'), where('eventId', '==', eventId));
        const snapshot = await getDocs(q);
        const antalTilmeldte = snapshot.docs.reduce((sum, doc) => 
          sum + doc.data().antalDeltagere, 0);
        setCurrentTilmeldte(antalTilmeldte);
      } catch (error) {
        console.error('Fejl ved hentning af tilmeldinger:', error);
      }
    };

    hentTilmeldinger();
  }, [eventId]);

  const maxTilladte = parseInt(maxDeltagere);

  const handleNext = () => {
    const ledigePladser = maxTilladte - currentTilmeldte;
    
    if (parseInt(formData.antalDeltagere) > ledigePladser) {
      alert(`Der er kun plads til ${ledigePladser} nye deltagere på dette kursus.`);
      return;
    }
    
    setFormData({
      ...formData,
      deltagere: Array.from({ length: parseInt(formData.antalDeltagere) }, () => ({
        email: '',
        navn: '',
        sagsnr: ''
      }))
    });
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleTilmeld = async () => {
    try {
      const tilmeldingData = {
        ...formData,
        eventId: eventId,
        oprettetDato: new Date().toISOString()
      };
      
      const docRef = await addDoc(collection(db, 'tilmeldinger'), tilmeldingData);

      const eventDoc = await getDoc(doc(db, 'events', eventId));
      const eventData = eventDoc.data();
      const kursusDoc = await getDoc(doc(db, 'kurser', eventData.kursusId));
      const kursusData = kursusDoc.data();

      await addDoc(collection(db, 'mail'), {
        to: ['info@byggerietspartner.dk'],
        message: {
          subject: `Ny kursustilmelding: ${kursusData.navn}`,
          html: `
            <!DOCTYPE html>
            <html>
              <head>
                <style>
                  body { 
                    font-family: Arial, sans-serif;
                    line-height: 1.6;
                    color: #333333;
                  }
                  .email-container {
                    max-width: 600px;
                    margin: 0 auto;
                    padding: 20px;
                  }
                  .header {
                    background-color: #58863B;
                    color: white;
                    padding: 30px;
                    border-radius: 8px 8px 0 0;
                    text-align: center;
                  }
                  .content {
                    background-color: #ffffff;
                    padding: 30px;
                    border: 1px solid #e0e0e0;
                    border-radius: 0 0 8px 8px;
                  }
                  .info-group {
                    margin-bottom: 20px;
                    padding-bottom: 20px;
                    border-bottom: 1px solid #eee;
                  }
                  .info-label {
                    color: #58863B;
                    font-weight: bold;
                    margin-bottom: 5px;
                  }
                  .info-value {
                    margin: 0;
                  }
                </style>
              </head>
              <body>
                <div class="email-container">
                  <div class="header">
                    <h1 style="margin:0;">Ny kursustilmelding</h1>
                  </div>
                  <div class="content">
                    <div class="info-group">
                      <div class="info-label">Kursus</div>
                      <p class="info-value">${kursusData.navn}</p>
                    </div>
                    
                    <div class="info-group">
                      <div class="info-label">Kursusdato</div>
                      <p class="info-value">${eventData.dato} - ${eventData.tid}</p>
                    </div>

                    <div class="info-group">
                      <div class="info-label">Virksomhed</div>
                      <p class="info-value">
                        ${formData.virksomhedsnavn}<br>
                        CVR: ${formData.cvr}
                      </p>
                    </div>
                    
                    <div class="info-group">
                      <div class="info-label">Fakturering</div>
                      <p class="info-value">
                        Type: ${formData.faktureringsType === 'email' ? 'Email' : 'EAN'}<br>
                        ${formData.faktureringsType === 'email' 
                          ? `Email: ${formData.faktureringsemail}`
                          : `EAN: ${formData.ean}`}
                      </p>
                    </div>

                    <div class="info-group">
                      <div class="info-label">Deltagere (${formData.antalDeltagere})</div>
                      ${formData.deltagere.map((deltager, index) => `
                        <div style="margin-top: 10px;">
                          <strong>Deltager ${index + 1}:</strong><br>
                          Navn: ${deltager.navn}<br>
                          Email: ${deltager.email}<br>
                          Sagsnr: ${deltager.sagsnr}
                        </div>
                      `).join('')}
                    </div>
                  </div>
                </div>
              </body>
            </html>
          `,
          text: `
Ny kursustilmelding

KURSUS
${kursusData.navn}

KURSUSDATO
${eventData.dato} - ${eventData.tid}

VIRKSOMHED
${formData.virksomhedsnavn}
CVR: ${formData.cvr}

FAKTURERING
Type: ${formData.faktureringsType === 'email' ? 'Email' : 'EAN'}
${formData.faktureringsType === 'email' 
  ? `Email: ${formData.faktureringsemail}`
  : `EAN: ${formData.ean}`}

DELTAGERE (${formData.antalDeltagere})
${formData.deltagere.map((deltager, index) => `
Deltager ${index + 1}:
Navn: ${deltager.navn}
Email: ${deltager.email}
Sagsnr: ${deltager.sagsnr}
`).join('\n')}
          `
        }
      });

      // Send bekræftelsesmail til hver deltager
      for (const deltager of formData.deltagere) {
        await addDoc(collection(db, 'mail'), {
          to: [deltager.email],
          message: {
            subject: `Bekræftelse på tilmelding: ${kursusData.navn}`,
            html: `
              <!DOCTYPE html>
              <html>
                <head>
                  <style>
                    body { 
                      font-family: Arial, sans-serif;
                      line-height: 1.6;
                      color: #333333;
                    }
                    .email-container {
                      max-width: 600px;
                      margin: 0 auto;
                      padding: 20px;
                    }
                    .header {
                      background-color: #58863B;
                      color: white;
                      padding: 30px;
                      border-radius: 8px 8px 0 0;
                      text-align: center;
                    }
                    .content {
                      background-color: #ffffff;
                      padding: 30px;
                      border: 1px solid #e0e0e0;
                      border-radius: 0 0 8px 8px;
                    }
                    .section {
                      margin-bottom: 30px;
                      padding-bottom: 20px;
                      border-bottom: 1px solid #eee;
                    }
                    .section-title {
                      color: #58863B;
                      font-size: 20px;
                      margin-bottom: 15px;
                    }
                    .button {
                      display: inline-block;
                      background-color: #58863B;
                      color: white;
                      padding: 12px 25px;
                      text-decoration: none;
                      border-radius: 5px;
                      margin-top: 15px;
                    }
                    .footer {
                      background-color: #f8f9fa;
                      padding: 20px;
                      border-radius: 8px;
                      margin-top: 30px;
                      font-size: 14px;
                    }
                  </style>
                </head>
                <body>
                  <div class="email-container">
                    <div class="header">
                      <h1 style="margin:0;">Tak for din tilmelding</h1>
                    </div>
                    <div class="content">
                      <div class="section">
                        <p>Kære ${deltager.navn},</p>
                        <p>Tak for din tilmelding til ${kursusData.navn}.</p>
                        <p><strong>Kursusdato:</strong> ${eventData.dato}<br>
                        <strong>Tidspunkt:</strong> ${eventData.tid}<br>
                        <strong>Lokation:</strong> ${eventData.lokation}</p>
                      </div>

                      <div class="section">
                        <div class="section-title">Kursusindhold</div>
                        ${kursusData.indhold}
                      </div>

                      <div class="section">
                        <div class="section-title">Praktisk information</div>
                        ${kursusData.praktiskInfo}
                      </div>

                      <div class="footer">
                        <p><strong>Vigtig information om afmelding:</strong></p>
                        <p>Din tilmelding er bindende. Hvis du ønsker at afmelde dig kurset, skal dette ske senest 5 dage før kursusstart. Ved senere afmelding eller udeblivelse faktureres det fulde kursusbeløb.</p>
                        <p>For afmelding eller spørgsmål, kontakt os venligst på:</p>
                        <a href="mailto:info@byggerietspartner.dk" class="button">Send mail til info@byggerietspartner.dk</a>
                      </div>
                    </div>
                  </div>
                </body>
              </html>
            `,
            text: `
Tak for din tilmelding

Kære ${deltager.navn},

Tak for din tilmelding til ${kursusData.navn}.

Kursusdato: ${eventData.dato}
Tidspunkt: ${eventData.tid}
Lokation: ${eventData.lokation}

KURSUSINDHOLD
${kursusData.indhold.replace(/<[^>]*>/g, '')}

PRAKTISK INFORMATION
${kursusData.praktiskInfo.replace(/<[^>]*>/g, '')}

VIGTIG INFORMATION OM AFMELDING
Din tilmelding er bindende. Hvis du ønsker at afmelde dig kurset, skal dette ske senest 5 dage før kursusstart. Ved senere afmelding eller udeblivelse faktureres det fulde kursusbeløb.

For afmelding eller spørgsmål, kontakt os venligst på: info@byggerietspartner.dk
            `
          }
        });
      }

      alert('Tilmelding gennemført!');
      if (onTilmeldingGennemført) {
        await onTilmeldingGennemført();
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Fejl ved tilmelding:', error);
      alert('Fejl ved tilmelding: ' + error.message);
    }
  };

  return (
    <div className="tilmeld-content">
      <div className="tilmeld-header">
        <h2>{step === 1 ? 'Virksomhedsoplysninger' : 'Deltageroplysninger'}</h2>
        <div className="tilmeld-step-indicator">
          <div className={`tilmeld-step ${step === 1 ? 'active' : ''}`} />
          <div className={`tilmeld-step ${step === 2 ? 'active' : ''}`} />
        </div>
      </div>

      {step === 1 ? (
        <div>
          <div className="tilmeld-form-group">
            <label className="tilmeld-label">Virksomhedsnavn</label>
            <input
              className="tilmeld-input"
              type="text"
              value={formData.virksomhedsnavn}
              onChange={(e) => setFormData({ ...formData, virksomhedsnavn: e.target.value })}
              required
            />
          </div>

          <div className="tilmeld-form-group">
            <label className="tilmeld-label">CVR-nummer</label>
            <input
              className="tilmeld-input"
              type="text"
              value={formData.cvr}
              onChange={(e) => setFormData({ ...formData, cvr: e.target.value })}
              required
            />
          </div>

          <div className="tilmeld-form-group">
            <label className="tilmeld-label">Faktureringsmetode</label>
            <div className="tilmeld-toggle-buttons">
              <button
                type="button"
                className={`tilmeld-toggle-button ${formData.faktureringsType === 'email' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, faktureringsType: 'email' })}
              >
                Email
              </button>
              <button
                type="button"
                className={`tilmeld-toggle-button ${formData.faktureringsType === 'ean' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, faktureringsType: 'ean' })}
              >
                EAN
              </button>
            </div>

            {formData.faktureringsType === 'email' ? (
              <div className="tilmeld-form-group">
                <label className="tilmeld-label">Faktureringsemail</label>
                <input
                  className="tilmeld-input"
                  type="email"
                  value={formData.faktureringsemail}
                  onChange={(e) => setFormData({ ...formData, faktureringsemail: e.target.value })}
                  required
                />
              </div>
            ) : (
              <div className="tilmeld-form-group">
                <label className="tilmeld-label">EAN-nummer</label>
                <input
                  className="tilmeld-input"
                  type="text"
                  value={formData.ean}
                  onChange={(e) => setFormData({ ...formData, ean: e.target.value })}
                  required
                />
              </div>
            )}
          </div>

          <div className="tilmeld-form-group">
            <label className="tilmeld-label">Antal deltagere</label>
            <input
              className="tilmeld-input"
              type="number"
              value={formData.antalDeltagere}
              onChange={(e) => {
                const værdi = parseInt(e.target.value) || 1;
                const ledigePladser = maxTilladte - currentTilmeldte;
                
                if (værdi <= ledigePladser && værdi > 0) {
                  setFormData({ ...formData, antalDeltagere: værdi });
                } else {
                  alert(`Der er kun plads til ${ledigePladser} nye deltagere på dette kursus.`);
                }
              }}
              min="1"
              max={maxTilladte - currentTilmeldte}
              required
            />
            <span className="tilmeld-info-text">
              Ledige pladser: {maxTilladte - currentTilmeldte}
            </span>
          </div>

          <div className="tilmeld-buttons">
            <button className="tilmeld-button" onClick={handleNext}>
              Fortsæt til deltageroplysninger →
            </button>
          </div>
        </div>
      ) : (
        <div>
          {formData.deltagere.map((_, index) => (
            <div key={index} className="tilmeld-deltager-card">
              <div className="tilmeld-deltager-header">
                Deltager {index + 1}
              </div>
              <div className="tilmeld-form-group">
                <label className="tilmeld-label">Email</label>
                <input
                  className="tilmeld-input"
                  type="email"
                  value={formData.deltagere[index].email}
                  onChange={(e) => {
                    const newDeltagere = [...formData.deltagere];
                    newDeltagere[index].email = e.target.value;
                    setFormData({ ...formData, deltagere: newDeltagere });
                  }}
                  required
                />
              </div>
              <div className="tilmeld-form-group">
                <label className="tilmeld-label">Navn</label>
                <input
                  className="tilmeld-input"
                  type="text"
                  value={formData.deltagere[index].navn}
                  onChange={(e) => {
                    const newDeltagere = [...formData.deltagere];
                    newDeltagere[index].navn = e.target.value;
                    setFormData({ ...formData, deltagere: newDeltagere });
                  }}
                  required
                />
              </div>
              <div className="tilmeld-form-group">
                <label className="tilmeld-label">Sagsnummer</label>
                <input
                  className="tilmeld-input"
                  type="text"
                  value={formData.deltagere[index].sagsnr}
                  onChange={(e) => {
                    const newDeltagere = [...formData.deltagere];
                    newDeltagere[index].sagsnr = e.target.value;
                    setFormData({ ...formData, deltagere: newDeltagere });
                  }}
                  required
                />
              </div>
            </div>
          ))}
          <div className="tilmeld-buttons">
            <button className="tilmeld-back-button" onClick={handleBack}>
              ← Tilbage
            </button>
            <button className="tilmeld-button" onClick={handleTilmeld}>
              Gennemfør tilmelding
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventTilmeld;
